import { Component, OnDestroy, OnInit } from '@angular/core';
import { EmergencyLightingTestService } from '@app/shared/services/emergency-lighting/emergency-lighting-test.service';
import { concatMap, interval, Observable, Subject, Subscription, switchMap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { filter, map, startWith, take, takeUntil } from 'rxjs/operators';
import { HeaderService } from '@services/header.service';
import { FloorplanService, TestsSummary } from '@services/floorplan.service';
import { environment } from '@environment';
import { EmDriverService } from '@services/em-driver.service';
import { ToastService } from '@services/toast/toast.service';
import { TagService } from '@services/tag.service';
import { FloorplanTag } from '@app/shared/models/tag.interface';
import { TagChangeEvent, TagsComponent } from '@components/tags/tags.component';
import { SensorNodeService } from '@services/sensor-node.service';
import { SelectableNode } from '@app/shared/models/sensor-node';
import { Selectable } from '@app/shared/models/selectable.interface';
import { PanelToggleComponent } from '@components/panel-toggle/panel-toggle.component';
import { AsyncPipe, NgClass } from '@angular/common';
import { FloorplanComponent } from '@components/floorplan/floorplan.component';
import { FailedTestsCounterComponent } from '@components/floorplan/failed-tests-counter/failed-tests-counter.component';

@Component({
  selector: 'app-emergency-lighting-manual-tests',
  templateUrl: './emergency-lighting-manual-tests.component.html',
  styleUrls: ['./emergency-lighting-manual-tests.component.scss'],
  imports: [PanelToggleComponent, NgClass, FloorplanComponent, FailedTestsCounterComponent, TagsComponent, AsyncPipe]
})
export class EmergencyLightingManualTestsComponent implements OnInit, OnDestroy {
  shouldShowCounter: boolean;
  latestEmResults$: Observable<TestsSummary>;
  tagPanelToggleState = true;
  floorPanelToggleState = true;
  buildingId: number;
  tags$: Observable<FloorplanTag[]>;
  private onDestroy$ = new Subject<void>();
  private timeInterval: Subscription;

  constructor(
    private floorplanService: FloorplanService,
    private readonly emDetailsService: EmergencyLightingTestService,
    private readonly emDriverService: EmDriverService,
    private readonly header: HeaderService,
    private readonly route: ActivatedRoute,
    private readonly snService: SensorNodeService,
    private readonly tagService: TagService,
    private readonly toast: ToastService
  ) {
    this.header.showFloorsMenu();
  }

  ngOnInit(): void {
    this.route.params
      .pipe(
        concatMap((p) => {
          this.buildingId = p.buildingId;
          this.tags$ = this.tagService.getTags(this.buildingId, true);
          return this.emDetailsService.getDetails(p.buildingId);
        })
      )
      .subscribe((details) => {
        this.shouldShowCounter = details.bannerEnabled;
        this.startPollingForReceivedNodes();
      });
    this.latestEmResults$ = this.floorplanService.emergencyResultSummary;
  }

  ngOnDestroy(): void {
    if (this.timeInterval) {
      this.timeInterval.unsubscribe();
    }
    this.floorplanService.setLatestEmergencyResults([]);
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private extractDriverIdsFromCurrentFloor$(): Observable<number[]> {
    return this.snService.getCurrentFloorNodes$().pipe(
      map((nodes) => nodes.flatMap((n) => n.emDrivers?.map((e) => e.id) ?? [])),
      filter((drivers) => drivers.length > 0)
    );
  }

  private fetchLatestEmergencyResultsOnce(): void {
    this.extractDriverIdsFromCurrentFloor$()
      .pipe(
        take(1),
        switchMap((drivers) => this.emDriverService.getEmergencyLightingTestResultsForDrivers(this.buildingId, drivers))
      )
      .subscribe({
        next: (value) => this.floorplanService.setLatestEmergencyResults(value),
        error: (err) => this.handleLatestResultsError(err)
      });
  }

  handleTestBatchRequest(): void {
    this.fetchLatestEmergencyResultsOnce();
  }

  private startPollingForReceivedNodes(): void {
    // reset the interval if floor or building changes
    if (this.timeInterval) {
      this.timeInterval.unsubscribe();
    }

    this.timeInterval = interval(environment.elmtPollingInterval)
      .pipe(
        startWith(null), // this is to simulate an initial stream from interval operator, that triggers the api to be called immediately
        switchMap(() => this.extractDriverIdsFromCurrentFloor$().pipe(take(1))),
        switchMap((drivers) =>
          this.emDriverService.getEmergencyLightingTestResultsForDrivers(this.buildingId, drivers)
        ),
        takeUntil(this.onDestroy$)
      )
      .subscribe({
        next: (value) => {
          this.floorplanService.setLatestEmergencyResults(value);
        },
        error: (err) => this.handleLatestResultsError(err)
      });
  }

  private handleLatestResultsError(err: unknown): void {
    this.toast.error({
      message: 'Latest ELMT results could not be fetched',
      autoClose: false,
      dismissible: true,
      dataCy: 'load-error-toast'
    });
    console.error(err);
  }

  toggleTagPanel(val: boolean): void {
    this.tagPanelToggleState = val;
  }

  toggleFloorPanel(val: boolean): void {
    this.floorPanelToggleState = val;
  }

  toggleTag(event: TagChangeEvent): void {
    const tag = event.tag;
    tag.isActive = event.isActive;
    this.floorplanService.tagToggled(tag);
  }

  handleNodeClick(node: SelectableNode): void {
    if (!node.connected || node.emDrivers == null || node.emDrivers?.length === 0) {
      return; // disconnected nodes and nodes without EM drivers should not be selectable on manual tests
    }
    if (node.selected && !this.floorplanService.isCumulativeModeActive) {
      return; // don't do anything if the SN/PN is already selected
    }
    if (this.floorplanService.isCumulativeModeActive) {
      this.snService.toggleEntitySelection(node, true);
      return;
    }
    let selectables: Selectable[] = [node];
    selectables = selectables.concat(node.emDrivers as Selectable[]);
    this.snService.selectEntitiesInArea(selectables);
    this.snService.updateQueryParams();
  }
}
